import React from 'react';

import { Box, Header, Pressable, ScrollView, Text } from '@rbilabs/universal-components';
import Downshift from 'downshift';

import UnstyledButton from 'components/unstyled-button';
import { t } from 'state/intl/translations';
import { theme } from 'styles/configure-theme';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

import {
  ClearIndicator,
  DropdownIndicator,
  IndicatorSeparator,
} from './mad-lib-select-accessories';
import { IFilter } from './types';

interface IIsOpen {
  isOpen: boolean;
}
const Container = Box.withConfig({
  zIndex: 2,
  alignItems: 'stretch',
  width: '100%',
  minWidth: 0,
});

const SelectContainer = Box.withConfig<IIsOpen>(p => ({
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  flexBasis: {
    base: '100%',
    md: '80%',
  },
  paddingX: {
    base: '$2',
    md: '$4',
  },
  borderBottomWidth: 0,
  borderWidth: '1px',
  borderColor: p.isOpen ? theme.token('border-color-default') : 'transparent',
}));

const SelectContent = Box.withConfig({
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  paddingY: {
    base: '$2',
    md: '$1',
  },
});

const BlackBar = Box.withConfig<{
  isOpen: boolean;
}>(p => ({
  width: '100%',
  backgroundColor: '__legacyToken.icon-default',
  visibility: p.isOpen ? 'hidden' : 'visible',

  height: {
    base: '1px',
    md: '3px',
  },
}));

const SelectToggle = UnstyledButton.withConfig({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flex: 1,
});

const SelectLabel = Text.withConfig({
  focusable: true,
  marginTop: 0,
  marginRight: 0,
  marginBottom: 0,
  textTransform: Styles.textTransform.headlines,
  variant: 'copyOne',

  letterSpacing: {
    md: 1,
  },

  marginLeft: {
    base: '$2',
    md: '$4',
  },
});

const CurrentSelection = Header.withConfig({
  flex: 1,
  color: '__legacyToken.text-default',
  textTransform: Styles.textTransform.headlines,
  variant: 'headerThreeLook',
  mb: 0,
  noOfLines: 1,
  letterSpacing: {
    md: 1,
  },
});

const MenuContainer = Box.withConfig<IIsOpen>(p => ({
  width: '100%',
  _android: {
    minHeight: p.isOpen ? '$24' : 0,
  },
}));

const MenuList = ScrollView.withConfig<IIsOpen>(p => ({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: Styles.zIndex.below,
  width: '100%',
  paddingY: p.isOpen ? '$1' : 0,
  backgroundColor: '__legacyToken.background-pattern',
  borderBottomRightRadius: 8,
  borderBottomLeftRadius: 8,
  borderTopWidth: 0,
  maxHeight: {
    base: 200,
    md: 320,
  },
  borderWidth: '1',
  borderColor: p.isOpen ? theme.token('border-color-default') : 'transparent',
}));

const SelectItem = Pressable.withConfig<{
  isSelected: boolean;
  isHighlighted: boolean;
}>(p => ({
  backgroundColor: p.isSelected
    ? '__legacyToken.background-active'
    : p.isHighlighted
    ? '__legacyToken.background-focus'
    : null,
  _web: {
    cursor: 'pointer',
  },
  _hover: {
    background: !p.isSelected ? theme.token('background-focus') : theme.token('background-active'),
  },
  paddingX: {
    base: '$2',
    md: '$4',
  },
  paddingY: {
    base: '$1',
    md: '$1',
  },
}));

const SelectItemText = Header.withConfig<{
  isSelected: boolean;
}>(p => ({
  margin: 0,
  textTransform: Styles.textTransform.headlines,
  variant: 'headerThree',
  letterSpacing: {
    md: 1,
  },
  color: p.isSelected ? '__legacyToken.text-reversed' : '__legacyToken.text-default',
}));

const itemToString = (filter: IFilter | null) => filter?.description?.locale ?? '';

interface IMadLibSelect {
  prompt?: string;
  selectedFilter: IFilter | null;
  filters: IFilter[];
  onFilterChange: (filter?: IFilter | null) => void;
}

export const MadLibSelect: React.FC<React.PropsWithChildren<IMadLibSelect>> = ({
  onFilterChange,
  selectedFilter,
  filters,
  prompt,
}) => {
  return (
    <Downshift
      itemToString={itemToString}
      initialSelectedItem={selectedFilter}
      onChange={changes => onFilterChange(changes)}
    >
      {({
        getRootProps,
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
        clearSelection,
      }) => {
        const currentSelection = selectedItem
          ? selectedItem.description?.locale
          : t('selectAFilter');

        // Downshift appears bugged on web now and this fixes it. We should remove it anyways as its only used here and very heavy!
        const buttonProps = getToggleButtonProps();
        const onPress = buttonProps.onPress || buttonProps.onClick;

        return (
          <Container {...getRootProps({} as any, { suppressRefError: true })}>
            <SelectLabel
              {...getLabelProps()}
              {...hiddenAccessibilityPlatformProps}
              accessibilityRole="header"
            >
              {prompt || t('imCraving')}
            </SelectLabel>
            <SelectContainer isOpen={isOpen}>
              <SelectContent>
                <SelectToggle {...{ ...buttonProps, onPress }} accessibilityRole="button">
                  <CurrentSelection>{currentSelection}</CurrentSelection>
                  <DropdownIndicator isOpen={isOpen} />
                </SelectToggle>
                <IndicatorSeparator />
                <ClearIndicator onPress={clearSelection} />
              </SelectContent>
              <BlackBar isOpen={isOpen} />
            </SelectContainer>
            <MenuContainer pointerEvents="auto" isOpen={isOpen}>
              <MenuList {...getMenuProps()} isOpen={isOpen}>
                {isOpen &&
                  filters.map((filter, index) => {
                    // Downshift appears bugged on web now and this fixes it. We should remove it anyways as its only used here and very heavy!
                    const itemProps = getItemProps({
                      item: filter,
                      index,
                    });
                    const onPress = itemProps.onPress || itemProps.onClick;

                    return (
                      <SelectItem
                        accessibilityRole="button"
                        key={filter._id}
                        isSelected={selectedItem?._id === filter._id}
                        isHighlighted={highlightedIndex === index}
                        {...{ ...itemProps, onPress }}
                      >
                        <SelectItemText isSelected={selectedItem?._id === filter._id}>
                          {filter.description?.locale}
                        </SelectItemText>
                      </SelectItem>
                    );
                  })}
              </MenuList>
            </MenuContainer>
          </Container>
        );
      }}
    </Downshift>
  );
};
export default MadLibSelect;
